import {ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal} from '@angular/core';
import {FormControl} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {lastValueFrom, switchMap} from 'rxjs';
import {PricePeriodSummary, ProductDetails, ProductSelect, ProductService} from '../../../../../api';
import {Dropdown} from '../../../../components/dropdown/dropdown';
import {MultiDropdown} from '../../../../components/multi-dropdown/multi-dropdown';
import {QueryKey} from '../../../../constants/query-key';

@Component({
  selector: 'elcia-invoice-product-dropdown',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Dropdown, MultiDropdown, TranslatePipe],
  templateUrl: './invoice-product-dropdown.html',
  styleUrl: './invoice-product-dropdown.scss',
})
export class InvoiceProductDropdown {
  private readonly productService = inject(ProductService);

  readonly control = input.required<FormControl>();
  readonly isMulti = input.required<boolean>();
  readonly legalEntityId = input.required<number | null>();
  readonly invoiceDate = input<Date | null>(null);

  readonly nameResolved = output<string>();
  readonly productSelected = output<ProductDetails | null>();
  readonly unitPriceResolved = output<number | null>();

  private readonly selectedProductId = signal<number | null>(null);
  private userChanged = false;

  private readonly productsQuery = injectQuery(() => ({
    queryKey: [QueryKey.PRODUCT_SELECT, this.legalEntityId()],
    queryFn: () => lastValueFrom(this.productService.getProductSelect(null, null, [this.legalEntityId()!])),
    enabled: this.legalEntityId() != null,
  }));

  private readonly productDetailsQuery = injectQuery(() => ({
    queryKey: [QueryKey.PRODUCT, this.selectedProductId()],
    queryFn: () => lastValueFrom(this.productService.getProductById(this.selectedProductId()!)),
    enabled: this.selectedProductId() != null,
  }));

  protected readonly products = computed<ProductSelect[]>(
    () => this.productsQuery.data()?.products ?? [],
  );

  constructor() {
    toObservable(this.control).pipe(
      switchMap(control => control.valueChanges),
      takeUntilDestroyed(),
    ).subscribe(productId => {
      this.userChanged = true;
      this.onProductIdChange(productId);
    });

    // Initialise depuis la valeur courante (mode édition) afin d'exposer le
    // produit sélectionné (ex : type de prestation) sans réécraser la
    // description ni le prix unitaire déjà persistés sur la ligne.
    effect(() => {
      const control = this.control();
      this.selectedProductId.set(control.value ?? null);
    });

    effect(() => {
      const productId = this.selectedProductId();
      const details = this.productDetailsQuery.data()?.product;
      if (productId == null || !details || details.id !== productId) return;
      this.productSelected.emit(details);
    });

    effect(() => {
      const productId = this.selectedProductId();
      const details = this.productDetailsQuery.data()?.product;
      const date = this.invoiceDate();
      if (!this.userChanged || productId == null || !details || details.id !== productId || !date) return;
      const price = this.getPriceForDate(details.pricePeriods, new Date(date));
      this.unitPriceResolved.emit(price);
    });
  }

  private onProductIdChange(productId: number | null): void {
    this.selectedProductId.set(productId ?? null);

    if (productId == null) {
      this.productSelected.emit(null);
      return;
    }

    const product = this.products().find(p => p.id === productId);
    if (product) {
      this.nameResolved.emit(product.name);
    }
  }

  private getPriceForDate(pricePeriods: PricePeriodSummary[], date: Date): number | null {
    const period = pricePeriods.find(p => {
      const start = new Date(p.startDate);
      const end = p.endDate ? new Date(p.endDate) : null;
      return date >= start && (end == null || date <= end);
    });
    return period?.price ?? null;
  }
}
